import React from 'react';
import { cssTransition } from 'react-toastify';
import './mikaToast.scss';

/**
 * Custom toast visuals for the 2026-07 redesign.
 *
 * - `renderToastIcon` is passed to <ToastContainer icon={...}> so every toast — no
 *   matter which of the ~700 `toast.success()/error()/…` call sites fired it — gets
 *   the ring-timer gauge with its type icon, without touching a single call site.
 * - `mikaToastTransition` is the spring entrance/exit.
 *
 * The ring is a CSS animation timed to the container's autoClose (4000ms) and paused
 * on hover, so it visually tracks react-toastify's own timer for the default duration.
 */

type ToastKind = 'success' | 'error' | 'warning' | 'info' | 'default';

const ICON_PATHS: Record<ToastKind, React.ReactNode> = {
    success: <path d="M20 6 9 17l-5-5" />,
    error: <path d="M18 6 6 18M6 6l12 12" />,
    warning: (
        <>
            <path d="M12 4 21 19H3z" />
            <path d="M12 10v4" />
            <path d="M12 17.4v.1" />
        </>
    ),
    info: (
        <>
            <circle cx="12" cy="12" r="9" />
            <path d="M12 11v5" />
            <path d="M12 7.9v.1" />
        </>
    ),
    default: <circle cx="12" cy="12" r="9" />,
};

export const ToastGauge: React.FC<{ kind: ToastKind }> = ({ kind }) => (
    <span className={`mika-gauge mika-gauge--${kind}`}>
        <svg className="mika-gauge__ring" viewBox="0 0 40 40" aria-hidden="true">
            <circle className="mika-gauge__track" cx="20" cy="20" r="17" />
            <circle className="mika-gauge__fg" cx="20" cy="20" r="17" />
        </svg>
        <svg
            className="mika-gauge__icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.4}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            {ICON_PATHS[kind]}
        </svg>
    </span>
);

// react-toastify calls this for every toast, passing { theme, type, isLoading }.
// Typed loosely to stay compatible across the library's icon-prop signature.
export const renderToastIcon = (props: any): React.ReactNode => {
    const type = props && typeof props.type === 'string' ? props.type : 'default';
    const kind: ToastKind = (['success', 'error', 'warning', 'info'].includes(type)
        ? type
        : 'default') as ToastKind;
    return <ToastGauge kind={kind} />;
};

export const mikaToastTransition = cssTransition({
    enter: 'mika-toast-in',
    exit: 'mika-toast-out',
    collapseDuration: 300,
});
